import { parse } from 'cookie';
import {
    AUTOTRANSLATED_LANGUAGES,
    SUPPORTED_LANGUAGES,
    type AppCountry,
    type AppLanguage,
    type AutoTranslatedLanguage
} from '../models/index.ts';
import { pick } from './accept-language-parser.ts';

export const getSupportedLanguages = (country: AppCountry): AppLanguage[] => {
    const languages = Array.isArray(SUPPORTED_LANGUAGES[country])
        ? (SUPPORTED_LANGUAGES[country] as unknown as AppLanguage[])
        : [SUPPORTED_LANGUAGES[country] as AppLanguage];
    return languages.filter((l) => !!l);
};

export const getAutoTranslatedLanguages = (country: AppCountry): AutoTranslatedLanguage[] => {
    const languages = Array.isArray(AUTOTRANSLATED_LANGUAGES[country])
        ? (AUTOTRANSLATED_LANGUAGES[country] as unknown as AutoTranslatedLanguage[])
        : [AUTOTRANSLATED_LANGUAGES[country] as unknown as AutoTranslatedLanguage];
    return languages.filter((l) => !!l);
};

export const isSupportedLanguage = (country: AppCountry, language: AppLanguage) => {
    const languages = getSupportedLanguages(country);
    return !!languages.find((l) => l === language);
};

export const isAutoTranslatedLanguage = (
    country: AppCountry,
    language: AppLanguage
): language is AutoTranslatedLanguage => {
    const languages = getAutoTranslatedLanguages(country);
    return !!languages.find((l) => l === language);
};

export const getLangFromWebview = (
    acceptLanguageHeader: string | undefined,
    country: AppCountry
): AppLanguage | null => {
    if (!acceptLanguageHeader) return null;
    const supportedLanguages = getSupportedLanguages(country);
    return pick(supportedLanguages as string[], acceptLanguageHeader) as AppLanguage | null;
};

export const getLangFromCookie = (cookies: string, country: AppCountry): AppLanguage | null => {
    const languageCookie = parse(cookies)['BaseDirectory'];
    if (!languageCookie) return null;

    const candidate = `${languageCookie.toLowerCase()}-${country.toUpperCase()}`;
    const supported = getSupportedLanguages(country);

    return supported.find((lang) => lang.toLowerCase() === candidate.toLowerCase()) ?? null;
};

export const getLangFromPath = (path?: string, country?: AppCountry): AppLanguage | null => {
    if (!path || !country) return null;
    const match = /^\/([a-z]{2})(\/|$)/i.exec(path);
    const prefix = match?.[1]?.toLowerCase();

    if (!prefix) return null;

    const supportedLanguages = getSupportedLanguages(country);
    return supportedLanguages.find((lang) => lang.toLowerCase().startsWith(prefix)) ?? null;
};

export const getLangFromQueryParam = (queryParam: string, country: AppCountry) => {
    const supported = getSupportedLanguages(country);
    const matched = supported.find((lang) => lang.toLowerCase().startsWith(`${queryParam}-${country}`.toLowerCase()));
    return matched;
};

export const getLangFromAutoTranslateHeader = (header: string, country: AppCountry) => {
    const supported = getSupportedLanguages(country).filter((lang) => isAutoTranslatedLanguage(country, lang));
    const matched = supported.find((lang) => lang.toLowerCase() === header.toLowerCase());
    return matched;
};

export const getFallbackLanguage = (country: AppCountry): AppLanguage => {
    const supportedLanguages = getSupportedLanguages(country);
    return supportedLanguages.length > 0 ? supportedLanguages[0] : 'fr-FR';
};

export const getCurrentLanguage = (opts: {
    country: AppCountry;
    cookies: string;
    path?: string;
    isFromWebview?: boolean;
    acceptLanguageHeader?: string;
    queryLang?: string;
    autoTranslateHeader?: string;
}): AppLanguage => {
    const { country, cookies, path, isFromWebview, acceptLanguageHeader, queryLang, autoTranslateHeader } = opts;

    if (queryLang) {
        const lang = getLangFromQueryParam(queryLang, country);
        if (lang) return lang;
    }

    if (isFromWebview) {
        const lang = getLangFromWebview(acceptLanguageHeader, country);
        if (lang) return lang;
    }

    if (autoTranslateHeader) {
        const lang = getLangFromAutoTranslateHeader(autoTranslateHeader, country);
        if (lang) return lang;
    }

    const pathLang = getLangFromPath(path, country);
    if (pathLang) return pathLang;

    const cookieLang = getLangFromCookie(cookies, country);
    if (cookieLang) return cookieLang;

    return getFallbackLanguage(country);
};
